<template>
  <BaseTable
    :isLoading="isLoading"
    :tableData="data"
    :totalItemsCount="totalItemsCount"
    :perPage="perPage"
    :currentPage="currentPage"
    :showSearch="true"
    searchPlaceholder="Search by name"
    :showCreateButton="rolPermissions.isAllowedCreate"
    createLabel="New Profile"
    @create="$emit('openModal')"
    @search="onSearch"
    @clear="onClear"
    @paginate="paginate"
  >
    <template #columns>
      <Column field="name" header="Name" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <p>{{ data.name }}</p>
        </template>
      </Column>
    </template>

    <template #actions-column>
      <Column header="Actions">
        <template #body="{ data }">
          <div class="data-table__actions">
            <span v-if="rolPermissions.isAllowedEdit" @click="$emit('openModal', data)">
              <i class="pi pi-pencil"></i>
            </span>
            <span v-if="rolPermissions.isAllowedDelete" @click="deleteRow(data)">
              <i class="pi pi-trash"></i>
            </span>
            <InputSwitch v-model="data.checked" @click="updateProfileStatus(data)" />
          </div>
        </template>
      </Column>
    </template>
  </BaseTable>
</template>

<script>
import { mapGetters } from "vuex";
import { successMessage, deleteMessage } from "@/utils/alertMessages";
import { rolPermissions } from "@/mixins/itemsTable/infoUser";
import BaseTable from "./BaseTable.vue";

export default {
  name: "ProfileTable",
  components: { BaseTable },
  props: ["firstPage"],
  emits: ["openModal"],
  data() {
    return {
      isLoading: false,
      data: [],
      loggedUser: null,
      rolPermissions: {},
      confirmationDeleteMessage: "I'm sure I want to delete this",
      totalItemsCount: 0,
      currentPage: 1,
      perPage: 15,
    };
  },
  computed: {
    ...mapGetters("user", ["showCurrentCompanyDepartment"]),
  },
  watch: {
    firstPage() {
      this.currentPage = 1;
    },
  },
  async created() {
    this.loggedUser = JSON.parse(localStorage.getItem("user-info"));
    this.getUserPermissions();
    await this.getprofiles();
  },
  methods: {
    getUserPermissions() {
      this.rolPermissions = rolPermissions(this.loggedUser, "Profile");
    },
    async getprofiles(params) {
      this.isLoading = true;
      const payload = { ...(params || {}) };
      payload.limit = 15;
      payload.skip = (this.currentPage - 1) * 15;
      const info = await this.$store.dispatch("profile/getAllProfiles", payload);
      this.data = info.data;
      this.totalItemsCount = info.count;
      this.data.forEach((profile, index) => {
        this.data[index].checked = profile.status === "active";
      });
      this.isLoading = false;
    },
    async onSearch(term) {
      if (term) {
        this.currentPage = 1;
        await this.getprofiles({ name: term });
      }
    },
    async onClear() {
      this.currentPage = 1;
      await this.getprofiles();
    },
    async paginate(page) {
      this.currentPage = page;
      await this.getprofiles();
      window.scrollTo(0, 0);
    },
    async deleteRow(profileData) {
      const msg = "Are you sure you want to remove Profile from the system?";
      const warningResponse = await deleteMessage(msg, "You won't be able to revert this!");
      if (warningResponse.isConfirmed) {
        const params = {
          profile_id: profileData.id,
          confirmation_message: this.confirmationDeleteMessage,
        };
        const response = await this.$store.dispatch("profile/deleteProfile", params);
        if (response.status === 200 || response.status === 204) {
          successMessage("The profile has been deleted.");
          if (this.data.length === 1 && this.currentPage > 1) this.currentPage -= 1;
          await this.getprofiles();
        }
      }
    },
    async updateProfileStatus(profile) {
      const newStatus = profile.status === "active" ? "inactive" : "active";
      const params = { profile_id: profile.id, data: { status: newStatus } };
      const response = await this.$store.dispatch("profile/updateProfile", params);
      if (response.status === 200 || response.status === 204) {
        successMessage("The profile has been updated.");
        await this.getprofiles();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@include table-action-icons;
</style>
